import type { PaymentMethod } from "@albertine/types/openapi";
import { Lib } from "albertine-shared-web";
import { type ReactNode, useState } from "react";
import Label from "../../Common/Label";

import "./PaymentCardList.css";
import classNames from "classnames";
import { brandToTextMap, type CardPaymentMethod } from "../../../common/types/Payments";
import Icon from "../../Common/Icon";
import Tag from "../../Common/Tag";

export type PaymentOption = {
    id: string;
    label: string;
    sublabel: ReactNode | null;
    type: PaymentMethod;
};

type PaymentCardListProps = {
    onSelectionChange: (option: PaymentOption) => void;
    cards: CardPaymentMethod[];
};

export function PaymentCardList({ onSelectionChange, cards }: PaymentCardListProps) {
    const [selectedId, setSelectedId] = useState<PaymentOption["id"] | null>(null);

    // All payment options
    const cardOptions: PaymentOption[] = cards.map((card) => ({
        id: card.id,
        label: `${brandToTextMap[card.brand]} *${card.last4}`,
        sublabel: (
            <Tag.Small.T90>
                Expires {card.expMonth}/{card.expYear}
            </Tag.Small.T90>
        ),
        type: "card" as const,
    }));

    const additionalOptions: PaymentOption[] = [
        {
            id: "payment-link",
            label: "Payment link for a new card",
            sublabel: null,
            type: "payment-link" as const,
        },
        {
            id: "other",
            label: "Other, add details below",
            sublabel: null,
            type: "other" as const,
        },
    ];

    const options: PaymentOption[] = cardOptions.concat(additionalOptions);

    const handleOptionSelect = (option: PaymentOption) => {
        if (selectedId === option.id) {
            setSelectedId(null);
            onSelectionChange({} as PaymentOption);
        } else {
            setSelectedId(option.id);
            onSelectionChange(option);
        }
    };

    return (
        <ul
            className="payment-card-list"
            role="listbox"
            aria-labelledby="card-selection-heading"
        >
            {options.map((option) => (
                <PaymentOptionItem
                    key={option.id}
                    option={option}
                    isSelected={selectedId === option.id}
                    onClick={() => {
                        handleOptionSelect(option);
                    }}
                />
            ))}
        </ul>
    );
}

type PaymentOptionItemProps = {
    option: PaymentOption;
    isSelected: boolean;
    onClick: () => void;
};

const PaymentOptionItem = ({ option, isSelected, onClick }: PaymentOptionItemProps) => {
    return (
        <li
            className={classNames("payment-card-item", {
                selected: isSelected,
            })}
            role="option"
            aria-selected={isSelected}
        >
            <div
                className="payment-card-button"
                onClick={onClick}
                role="button"
                tabIndex={0}
            >
                <Lib.Flex.Row
                    className="payment-card-content"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Lib.Flex.Row
                        className="payment-card-details"
                        gap="value12"
                        justifyContent="start"
                        alignItems="center"
                    >
                        {getIcon(option.type)}

                        <Label.Small.SemiBold.N10>
                            {option.label}
                        </Label.Small.SemiBold.N10>

                        {option.sublabel && (
                            <Label.Small.Normal.N10>
                                {option.sublabel}
                            </Label.Small.Normal.N10>
                        )}
                    </Lib.Flex.Row>

                    <div className="payment-card-checkmark-container">
                        <Icon.Check.Medium.N100 />
                    </div>
                </Lib.Flex.Row>
            </div>
        </li>
    );
};

function getIcon(type: PaymentOption["type"]) {
    if (type === "payment-link") {
        return <Icon.Link.Medium.N10 />;
    }

    if (type === "other") {
        return <Icon.AttachMoney.Medium.N10 />;
    }

    return <Icon.CreditCard.Medium.N10 />;
}
